import React, { Component } from 'react';
import { Column, ColumnProps } from 'primereact/column';
import { DataTable, DataTablePFSEvent, DataTableProps, DataTableRowClassNameOptions, DataTableRowClickEventParams } from 'primereact/datatable';
import { query } from '@trlogic/client-js';
import Icon from 'icons/Icon';
import CopyPasteUtil from 'util/CopyPasteUtil';
import ComponentUtil from '../../util/ComponentUtil';
import ComponentName from '../../constant/ComponentName';
import { Tooltip } from 'primereact/tooltip';
import Order = query.Order;
import { generateUUID } from '../../util/Generator';
import LanguageUtil from '../../util/LanguageUtil';

class CustomTable extends Component<Props, State> {
  public state: State = new State();

  private uuid = generateUUID();

  public render() {
    const { value, selectionMode, multiSortMeta, children, columns, onRowClick, onContextMenu, rowClassName, onSort, onSortChange, ...rest } = this.props;
    const { page } = this.state;
    return (
      <div className={'w-full h-full'}>
        {value?.length ? (
          <DataTable
            style={{ zIndex: 0 }}
            className={`w-full !text-f-black-3 !font-formica !text-md border-[1px] border-f-black-5 rounded-lg`}
            value={value}
            size='small'
            scrollable={true}
            scrollHeight={'flex'}
            reorderableColumns
            responsiveLayout='scroll'
            paginatorClassName='float-right'
            selectionMode={selectionMode}
            loadingIcon='pi pi-spin pi-spinner'
            filterDisplay='menu'
            onContextMenu={this.onContextMenu}
            sortMode='multiple'
            onSort={this.onSort}
            resizableColumns
            columnResizeMode='expand'
            onRowClick={this.onRowClick}
            rowClassName={this.onRowClassName}
            {...page}
            {...rest}
          >
            {selectionMode == 'checkbox' ? (
              <Column
                className={'!pl-5'}
                selectionMode='multiple'
                headerClassName={'!bg-main-gray 2xl:!h-[52px] 2xl:!max-h-[52px] !h-[44px] !max-h-[44px] !pl-20'}
              />
            ) : null}
            {this.getColumns()}
          </DataTable>
        ) : (
          <div className={'w-full h-[100%] flex flex-col items-center justify-center'}>
            <p className={'text-md'}>{LanguageUtil.get({ id: 'common.label.noData', defaultMessage: 'No Data' })}</p>
            <svg width='100' height='101' viewBox='0 0 100 101' fill='none' xmlns='http://www.w3.org/2000/svg'>
              <path
                d='M87.6261 20.6272C88.8465 19.4068 88.8465 17.4282 87.6261 16.2078C86.4057 14.9874 84.4271 14.9874 83.2067 16.2078L16.54 82.8744C15.3196 84.0948 15.3196 86.0735 16.54 87.2939C17.7604 88.5142 19.739 88.5142 20.9594 87.2939L29.6274 78.6259H77.1799C87.4837 78.6259 95.8365 70.2731 95.8365 59.9693C95.8365 49.6656 87.4837 41.3128 77.1799 41.3128C76.9132 41.3128 76.6478 41.3184 76.3836 41.3296C76.2264 38.6267 75.593 36.0517 74.5661 33.6872L87.6261 20.6272Z'
                fill='#BDBDBD'
              />
              <path
                d='M53.8827 20.1188C58.4947 20.1188 62.7832 21.5041 66.3549 23.8812C66.8836 24.2331 66.9367 24.9777 66.4876 25.4269L17.0681 74.8464C16.7358 75.1787 16.2209 75.2493 15.8208 75.0028C8.82599 70.6933 4.16309 62.9637 4.16309 54.145C4.16309 40.6246 15.1235 29.6642 28.6439 29.6642C30.8092 29.6642 32.9122 29.946 34.9165 30.476C38.9238 24.2497 45.9184 20.1188 53.8827 20.1188Z'
                fill='#BDBDBD'
              />
            </svg>
          </div>
        )}
        <Tooltip target={`.copy_${this.uuid}`}>{LanguageUtil.get({ id: 'common.label.copy', defaultMessage: 'Copy' })}</Tooltip>
        <Tooltip target={`.filter_${this.uuid}`}>{LanguageUtil.get({ id: 'common.label.filter', defaultMessage: 'Filter' })}</Tooltip>
      </div>
    );
  }

  public componentDidMount() {
    const { multiSortMeta } = this.props;
    if (multiSortMeta) {
      this.setState({ page: { ...this.state.page, multiSortMeta } });
    }
  }

  private getColumns = () => {
    const { columns } = this.props;
    return columns.map((c: ColumnProps, i) => {
      const props = this.getDefaultColumnProps(c as any);
      const key = c.columnKey ?? c.field ?? `column-${i}`;
      return <Column key={key} field={c.field} sortable header={c.header} {...props} {...c} body={props.body} />;
    });
  };

  private getDefaultColumnProps = (c: CustomColumnProps): ColumnProps => {
    const headStyle = { color: '#4F4F4F', backgroundColor: '#F9FAFB', fontSize: 13, fontWeight: 500 };
    const props: ColumnProps = {
      align: 'left',
      alignHeader: 'left',
      filterField: c.field,
      headerStyle: headStyle,
      bodyClassName: 'text-f-black-2 !h-[44px] 2xl:!h-[52px] m-0 p-0',
      columnKey: c.field,
      headerClassName: 'h-[44px] 2xl:h-[52px]',
      body: c.body,
    };
    if (c.copyable || c.filterable) {
      props.body = this.wrapWithIcon(c);
    }
    return props;
  };

  private wrapWithIcon = (c: CustomColumnProps) => {
    return (data: any, options: any) => {
      let element;
      if (typeof c.body == 'function') element = c.body(data, options);
      else if (typeof c.body == 'string') element = c.body;
      else element = data[c.field!];
      return (
        <div className={'flex items-center justify-start group m-0 p-0'}>
          {element}
          {c.copyable && (
            <div
              key={this.uuid + c.field}
              className={`copy_${this.uuid} active:bg-[#458EF714] active:rounded cursor-pointer flex items-center p-1 opacity-0 group-hover:opacity-100 ml-2`}
              onClick={(e) => this.onCopy(e, data, c.onCopy)}
            >
              <Icon icon='content_copy' size={12} color='#458EF7' />
            </div>
          )}
          {c.filterable && (
            <div
              className={`filter_${this.uuid} active:bg-[#458EF714] active:rounded cursor-pointer flex items-center p-1 opacity-0 group-hover:opacity-100 ml-2`}
              onClick={(e) => this.addFilter(e, data, c.addFilter)}
            >
              <Icon icon='filter_icon' size={16} color='#458EF7' />
            </div>
          )}
        </div>
      );
    };
  };

  private onCopy = (e: React.MouseEvent<HTMLDivElement>, data: any, onCopy: CustomColumnProps['onCopy']) => {
    e.preventDefault();
    e.stopPropagation();
    const value = onCopy?.(data);
    if (value == undefined) return;
    CopyPasteUtil.copy(value);
  };

  private addFilter = (e: React.MouseEvent<HTMLDivElement>, data: any, addFilter: CustomColumnProps['addFilter']) => {
    e.preventDefault();
    e.stopPropagation();
    addFilter?.(data);
  };

  private onRowClick = (e: DataTableRowClickEventParams) => {
    const { onRowClick, selection, selectionMode } = this.props;
    if (((Array.isArray(selection) && selection?.indexOf(e.data) != -1) || selection == e.data) && selectionMode != 'single') {
      // TODO fix this
      return;
    }

    if (typeof e.originalEvent.target != 'object') {
      const isCheckbox = (e.originalEvent.target as HTMLButtonElement)?.className.includes('p-checkbox');
      if (isCheckbox) return e.originalEvent.stopPropagation();
    }
    if (onRowClick) onRowClick(e);
  };

  private onRowClassName = (data: any, options: DataTableRowClassNameOptions) => {
    const other = this.props.rowClassName?.(data, options);
    return `${other} custom-table-row cursor-pointer !h-[44px] !max-h-[44px] 2xl:!h-[52px] 2xl:!max-h-[52px] box-border select-none`;
  };

  private onSort = (e: DataTablePFSEvent) => {
    const { page } = this.state;
    if (page.multiSortMeta == null) {
      page.multiSortMeta = [];
    }
    if (!Array.isArray(e.multiSortMeta)) return;
    const field = page.multiSortMeta.find((m) => m.field == e.multiSortMeta?.[0]?.field);
    if (field == undefined) page.multiSortMeta.push(e.multiSortMeta[0]);
    else {
      field.order = field.order == 0 ? 1 : field.order == 1 ? -1 : 0;
      if (field.order == 0) {
        const index = page.multiSortMeta.indexOf(field);
        if (index != -1) {
          page.multiSortMeta.splice(index, 1);
        }
      }
    }
    this.setState({ page }, () => {
      return this.props.onSortChange?.(
        (page.multiSortMeta || []).map((m) => ({
          field: m.field,
          operator: m.order == -1 ? 'DESC' : 'ASC',
        })) as Order[],
      );
    });
  };

  private onContextMenu = (e: any) => {
    const { componentName } = this.props;
    if (!this.props.openNewTab || componentName == undefined) return;
    e.originalEvent.stopPropagation();
    e.originalEvent.preventDefault();
    const existingContextMenu = document.querySelector('.context-menu');
    if (existingContextMenu) {
      existingContextMenu.remove();
    }
    const contextMenu = document.createElement('div');
    contextMenu.classList.add('context-menu');
    contextMenu.style.left = `${e.originalEvent.clientX}px`;
    contextMenu.style.top = `${e.originalEvent.clientY}px`;
    const openInNewTabOption = document.createElement('div');
    openInNewTabOption.classList.add('context-menu-item');
    openInNewTabOption.textContent = 'Open Component in New Tab';
    openInNewTabOption.onclick = () => {
      const url = ComponentUtil.handleLink(componentName, e.data.name, e.data);
      window.open(url, '_blank');
      contextMenu.remove();
    };
    contextMenu.appendChild(openInNewTabOption);
    document.body.appendChild(contextMenu);
    const removeContextMenu = () => {
      contextMenu.remove();
      document.removeEventListener('click', removeContextMenu);
    };
    document.addEventListener('click', removeContextMenu);
  };
}

interface Props extends DataTableProps {
  columns: ColumnProps[];
  onSortChange?: (sort: Order[]) => void;
  openNewTab?: boolean;
  componentName?: ComponentName;
}

export interface CustomColumnProps extends ColumnProps {
  copyable?: boolean;
  onCopy?: (data: any) => string | undefined;
  filterable?: boolean;
  addFilter?: (data: any) => void;
}

class State {
  page: DataTablePFSEvent = {
    first: 0,
    rows: 10,
    filters: {},
    sortField: '',
    sortOrder: null,
    multiSortMeta: null,
  };
}

export default CustomTable;
